/**
 * Amend Attachments Tab
 *
 * Tab component for managing attachments in the amend flow
 */

import { useState, useCallback } from 'react'
import { FileText, Image, Trash2, RefreshCcw } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/presentation/components/ui/Card'
import { Button } from '@/presentation/components/ui/Button'
import { cn } from '@/lib/utils'
import {
  AttachmentUploader,
  type AttachmentFile,
  prepareAttachmentsForUpload,
} from '@/presentation/components/studies/AttachmentUploader'
import type { AmendStudyData, AmendAttachmentInfo } from '@/domain/study/types'
import { ATTACHMENT_TYPE_LABELS, type AttachmentType } from '@/config/constants'

interface AmendAttachmentsTabProps {
  studyId: string
  attachments: AmendAttachmentInfo[]
  deleteAttachmentIds: string[]
  onDeleteChange: (ids: string[]) => void
  newAttachments: NonNullable<AmendStudyData['newAttachments']>
  onNewAttachmentsChange: (attachments: AmendStudyData['newAttachments']) => void
}

export function AmendAttachmentsTab({
  attachments,
  deleteAttachmentIds,
  onDeleteChange,
  onNewAttachmentsChange,
}: AmendAttachmentsTabProps) {
  const [uploaderFiles, setUploaderFiles] = useState<AttachmentFile[]>([])

  const toggleDelete = (id: string) => {
    if (deleteAttachmentIds.includes(id)) {
      onDeleteChange(deleteAttachmentIds.filter((i) => i !== id))
    } else {
      onDeleteChange([...deleteAttachmentIds, id])
    }
  }

  const handleUploaderChange = useCallback(
    (files: AttachmentFile[]) => {
      setUploaderFiles(files)
      const prepared = prepareAttachmentsForUpload(files)
      onNewAttachmentsChange(prepared)
    },
    [onNewAttachmentsChange]
  )

  const formatFileSize = (bytes: number | null): string => {
    if (!bytes) return '-'
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  const getFileIcon = (mimeType: string | null) => {
    if (mimeType?.startsWith('image/')) return Image
    return FileText
  }

  return (
    <div className="space-y-6">
      {/* Current Attachments */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Adjuntos Actuales ({attachments.length})</span>
            {deleteAttachmentIds.length > 0 && (
              <span className="text-sm font-normal text-red-600 dark:text-red-400">
                {deleteAttachmentIds.length} marcado(s) para eliminar
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {attachments.length === 0 ? (
            <p className="text-[var(--foreground-muted)] text-center py-8">
              No hay adjuntos en este estudio
            </p>
          ) : (
            <div className="space-y-2">
              {attachments.map((attachment) => {
                const isMarkedForDelete = deleteAttachmentIds.includes(attachment.id)
                const Icon = getFileIcon(attachment.mimeType)

                return (
                  <div
                    key={attachment.id}
                    className={cn(
                      'flex items-center gap-3 p-3 rounded-lg border transition-all',
                      isMarkedForDelete
                        ? 'border-red-300 bg-red-50 dark:bg-red-950/20 opacity-60'
                        : 'border-[var(--border)] bg-[var(--background-subtle)]'
                    )}
                  >
                    <div className="p-2 rounded bg-[var(--secondary)]">
                      <Icon className="h-5 w-5 text-[var(--foreground-muted)]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p
                        className={cn(
                          'font-medium text-sm truncate',
                          isMarkedForDelete && 'line-through'
                        )}
                      >
                        {attachment.fileName}
                      </p>
                      <p className="text-xs text-[var(--foreground-muted)]">
                        {ATTACHMENT_TYPE_LABELS[attachment.attachmentType as AttachmentType] ||
                          attachment.attachmentType}
                        {' - '}
                        {formatFileSize(attachment.fileSize)}
                      </p>
                    </div>
                    <Button
                      variant={isMarkedForDelete ? 'outline' : 'ghost'}
                      size="sm"
                      onClick={() => toggleDelete(attachment.id)}
                      className={isMarkedForDelete ? 'text-green-600 dark:text-green-400' : 'text-red-500'}
                    >
                      {isMarkedForDelete ? 'Restaurar' : <Trash2 className="h-4 w-4" />}
                    </Button>
                  </div>
                )
              })}
            </div>
          )}
          {deleteAttachmentIds.length > 0 && (
            <div className="mt-4 flex justify-end">
              <Button variant="outline" size="sm" onClick={() => onDeleteChange([])}>
                <RefreshCcw className="h-4 w-4 mr-2" />
                Desmarcar todos
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add New Attachments */}
      <Card>
        <CardHeader>
          <CardTitle>Agregar Nuevos Adjuntos</CardTitle>
        </CardHeader>
        <CardContent>
          <AttachmentUploader
            attachments={uploaderFiles}
            onChange={handleUploaderChange}
            minRequired={0}
            maxAllowed={10}
          />
        </CardContent>
      </Card>
    </div>
  )
}
